import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TechChip } from '@/components/ui/TechChip';
import { ProjectCard } from '@/components/ui/ProjectCard';
import { Reveal, Stagger, StaggerItem } from '@/components/ui/Reveal';
import type { Project, SkillGroup } from '@/lib/types';
import { useI18n } from '@/store/preferences';

interface WorkAndStackProps {
  projects: Project[];
  skills: SkillGroup[];
}

/**
 * Work and stack in one section rather than two stacked lists. The projects
 * are the evidence and the stack is the summary of that same evidence, so
 * separating them made the page read as an endless scroll of unrelated blocks.
 * On desktop the stack sits in a sticky rail beside the work.
 */
export function WorkAndStack({ projects, skills }: WorkAndStackProps) {
  const { t } = useI18n();

  const featured = projects.filter((project) => project.isFeatured);
  const showcase = (featured.length > 0 ? featured : projects).slice(0, 4);

  const groups = skills.filter((group) => group.items.length > 0);
  const totalTech = groups.reduce((sum, group) => sum + group.items.length, 0);

  return (
    <section className="section" id="work">
      <div className="shell">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_260px] lg:gap-16">
          {/* ── Work ───────────────────────────────────────────────────── */}
          <div className="order-2 lg:order-1">
            <Reveal className="mb-10">
              <p className="eyebrow mb-4">
                <span className="h-px w-6 bg-line" aria-hidden />
                {t.sections.selectedWork}
              </p>
              <h2 className="h2 text-balance">{t.sections.selectedWorkTitle}</h2>
            </Reveal>

            <Stagger className="space-y-5">
              {showcase.map((project, index) => (
                <StaggerItem key={project.id} index={index}>
                  <ProjectCard project={project} />
                </StaggerItem>
              ))}
            </Stagger>

            <Reveal className="mt-10">
              <Link to="/projects" className="btn-outline group">
                {t.sections.allProjects}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>
            </Reveal>
          </div>

          {/* ── Stack rail ─────────────────────────────────────────────── */}
          <aside className="order-1 lg:order-2 lg:sticky lg:top-28 lg:self-start">
            <Reveal>
              <div className="mb-5 flex items-baseline justify-between gap-3 border-b border-line pb-3">
                <p className="eyebrow">{t.sections.skills}</p>
                <span className="font-mono text-2xs tabular text-faint">{totalTech}</span>
              </div>

              <div className="space-y-6">
                {groups.map((group) => (
                  <div key={group.category}>
                    <h3 className="mb-3 font-mono text-2xs uppercase tracking-[0.18em] text-faint">
                      {t.category[group.category]}
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {group.items.map((tech) => (
                        <TechChip key={tech.id} tech={tech} size="sm" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <p className="mt-6 border-t border-line pt-4 text-xs leading-relaxed text-faint">
                {t.sections.skillsIntro}
              </p>
            </Reveal>
          </aside>
        </div>
      </div>
    </section>
  );
}
